const REVERT_MESSAGES: Record<string, string> = {
  LAUNCH_FEE: 'Factory launchFee() does not match the ETH sent with this create.',
  IMAGE: 'Token image URL is too long for the factory (512 characters max).',
  NAME: 'Name must be 1–64 characters.',
  TICKER: 'Ticker must be 1–12 characters.',
  DURATION: 'Duration must be between 1 and 30 days.',
  LP_PCT: 'LP percent must be between 70 and 100.',
  SOFT_CAP: 'Soft cap is below the factory minimum.',
  CAPS: 'Hard cap must be greater than or equal to soft cap.',
  WALLET_CAPS: 'Per-wallet min must be greater than 0 and max must be at least min.',
  MAX_GT_HARD: 'Per-wallet max cannot exceed the hard cap.',
  SUPPLY_MIN: 'Token supply is below the factory minimum.',
  SUPPLY_MAX: 'Token supply is above the factory maximum.',
  SUPPLY_SPLIT: 'Token supply cannot split 70/20/10 exactly.',
  RATE: 'Token supply must be greater than 0 so tokens per ETH can be derived from supply ÷ hard cap.',
  LISTING: 'Derived listing rate must be greater than 0.',
  LIST_RATE: 'Derived listing rate must be greater than 0 and not higher than tokens per ETH.',
  MIN_TOKENS: 'Min contribution must mint at least 1 token at the presale rate.',
  SOFT_RATIO: 'Hard cap cannot be more than 4× the soft cap.',
  STARTS: 'Start time must be at least 20 minutes from now on-chain.',
  CREATOR_PCT: 'Creator ETH percent must be between 0 and 30.',
  DELAY: 'Start date is too far in the future for the factory.',
  NOT_ENDED: 'This presale has not ended yet. Finalize is available after the end date if the soft cap was met, or sooner if the hard cap is filled.',
  SOFT: 'Soft cap was not reached, so tokens cannot be claimed.',
  CLAIMED: 'These tokens were already claimed.',
  NOTHING: 'Nothing to claim or refund for this wallet.',
  ETH_TRANSFER: 'Factory could not send the launch fee to the protocol wallet.',
  ADDR: 'A required contract address is missing on-chain.',
  INIT: 'Token or presale initialize failed on-chain.',
  NOT_LIVE: 'This presale is not live on-chain yet, or it has already closed.',
  DONE: 'This presale is already finalized.',
  ZERO: 'Enter an amount greater than 0.',
  HARD_CAP: 'This amount would exceed the hard cap.',
  WALLET_MAX: 'This amount would exceed the per-wallet maximum.',
  WALLET_MIN: 'Contribution must meet the on-chain per-wallet minimum.',
  CLOSED: 'Withdrawals are only open before this presale closes, or after the creator cancels.',
  OVER: 'You cannot withdraw more than your pledged ETH.',
  CANCELLED: 'This presale was cancelled. Contributors can still withdraw or refund pledged ETH.',
  NOT_CREATOR: 'Only the creator can cancel this presale.',
  STARTED: 'Creators can cancel before start, or only with more than 24 hours left.',
}

function pushText(parts: string[], value: unknown) {
  if (typeof value === 'string' && value.trim())
    parts.push(value.trim())
}

function collectErrorText(error: unknown): string {
  const parts: string[] = []
  const seen = new Set<unknown>()
  const queue: unknown[] = [error]
  while (queue.length) {
    const current = queue.shift()
    if (current == null || seen.has(current))
      continue
    if (typeof current === 'string' || typeof current === 'number' || typeof current === 'boolean') {
      pushText(parts, String(current))
      continue
    }
    if (typeof current !== 'object')
      continue
    seen.add(current)
    const record = current as Record<string, unknown>
    for (const key of ['shortMessage', 'message', 'details', 'reason', 'docsPath', 'errorName', 'name', 'code'])
      queue.push(record[key])
    const meta = record.metaMessages
    if (Array.isArray(meta))
      meta.forEach(item => queue.push(item))
    if (record.data && typeof record.data === 'object') {
      const data = record.data as { message?: unknown, errorName?: unknown, revert?: { args?: unknown[] } }
      queue.push(data.message, data.errorName)
      if (Array.isArray(data.revert?.args))
        data.revert.args.forEach(arg => queue.push(arg))
    }
    queue.push(record.cause, record.error, record.innerError)
    const walk = record.walk
    if (typeof walk === 'function') {
      try {
        walk.call(current, (nested: unknown) => queue.push(nested))
      }
      catch {
        // ignore walk failures on non-viem objects
      }
    }
  }
  return parts.join('\n')
}

function errorCode(error: unknown): number | string | undefined {
  if (!error || typeof error !== 'object' || !('code' in error))
    return undefined
  const code = (error as { code?: unknown }).code
  return typeof code === 'number' || typeof code === 'string' ? code : undefined
}

function isAbiSignatureContext(upper: string, index: number): boolean {
  const lineStart = upper.lastIndexOf('\n', index) + 1
  const lineEnd = upper.indexOf('\n', index)
  const line = upper.slice(lineStart, lineEnd === -1 ? upper.length : lineEnd)
  if (line.includes('FUNCTION:') || line.includes('CREATEPRESALE(') || line.includes('CONTRACT CALL'))
    return true
  const before = upper.slice(Math.max(0, index - 24), index)
  return /(?:STRING|BYTES\d*|UINT\d*|INT\d*|ADDRESS|BOOL|FUNCTION)\s+$/.test(before)
}

function mappedRevert(text: string): string | null {
  const upper = text.toUpperCase()
  for (const [code, message] of Object.entries(REVERT_MESSAGES)) {
    const token = new RegExp(`(?:^|[^A-Z0-9_])${code}(?:[^A-Z0-9_]|$)`, 'g')
    let match: RegExpExecArray | null
    while ((match = token.exec(upper))) {
      const tokenIndex = match.index + (match[0].startsWith(code) ? 0 : match[0].length - code.length)
      if (isAbiSignatureContext(upper, tokenIndex))
        continue
      return message
    }
  }
  return null
}

function isSimulationFailure(text: string): boolean {
  const lower = text.toLowerCase()
  return lower.includes('failed to simulate')
    || lower.includes('fail to simulate')
    || lower.includes('simulation failed')
    || lower.includes('cannot estimate')
    || lower.includes('estimate gas')
    || lower.includes('intrinsic gas')
    || lower.includes('execution reverted')
    || lower.includes('reverted with')
    || lower.includes('contractfunctionexecutionerror')
    || (lower.includes('contract function') && lower.includes('reverted'))
    || lower.includes('unexpected error')
    || Boolean(mappedRevert(text))
}

/** Robinhood RPC / wallets often fail eth_call for the two-contract createPresale deploy. */
export function isOpaqueRpcSimulationError(error: unknown): boolean {
  const text = collectErrorText(error)
  if (mappedRevert(text))
    return false
  return isSimulationFailure(text)
}

export function isUserRejectedError(error: unknown): boolean {
  const text = collectErrorText(error)
  if (isSimulationFailure(text))
    return false
  const code = errorCode(error)
  if (code === 4001 || code === 'ACTION_REJECTED' || code === '4001')
    return true
  const lower = text.toLowerCase()
  return lower.includes('user rejected')
    || lower.includes('user denied')
    || lower.includes('rejected the request')
    || lower.includes('denied transaction')
    || lower.includes('request rejected')
}

function isTimeoutError(text: string): boolean {
  const lower = text.toLowerCase()
  return lower.includes('timed out')
    || lower.includes('timeout')
    || lower.includes('taking too long')
    || lower.includes('did not confirm this transaction')
    || lower.includes('did not respond')
}

export function formatWalletError(error: unknown, fallback = 'Transaction failed'): string {
  const text = collectErrorText(error)
  const revert = mappedRevert(text)
  if (revert)
    return revert
  if (isUserRejectedError(error))
    return 'Wallet request cancelled. Confirm in your wallet to continue.'
  if (isTimeoutError(text))
    return 'Confirmation is taking too long. Check your wallet, then retry if the transaction did not land.'
  if (isOpaqueRpcSimulationError(error) || isSimulationFailure(text))
    return 'Wallet could not simulate this transaction. Confirm in the wallet if it is still open, then retry.'
  const firstLine = text.split('\n').map(line => line.trim()).find(line => (
    line
    && !line.toLowerCase().includes('request arguments')
    && !line.toLowerCase().includes('user rejected')
  )) ?? ''
  if (firstLine && firstLine.length <= 180)
    return firstLine
  return fallback
}

/**
 * View reads (minContribution, live limits) must never surface as a wallet simulation
 * failure or disable a send button. Do not replace this with formatWalletError.
 * See docs/presale-wallet-workflow.md §4 and §6 rules 9–10.
 */
export function formatViewReadError(error: unknown, fallback: string): string {
  const text = collectErrorText(error)
  const revert = mappedRevert(text)
  if (revert)
    return revert
  if (isOpaqueRpcSimulationError(error) || isSimulationFailure(text))
    return fallback
  return fallback
}
